import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { IonInput, ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppComponent } from '../../app.component';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Address from '../../../smoothr-web-app-core/models/Address';
import { MapsUtils } from '../../../smoothr-web-app-core/utils/maps-utils';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';
import { environment } from 'src/environments/environment';
import { calculateGeoDistance } from 'src/map/utils/maps';
import { ValidationUtils } from '../../../smoothr-web-app-core/utils/validation-utils';
import { AddressInputDialog } from '../address-input-dialog/address-input-dialog.component';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import GeocoderResult = google.maps.GeocoderResult;
import Map = google.maps.Map;

@Component({
	selector: 'app-check-delivery-radius-modal',
	templateUrl: './check-delivery-radius-modal.component.html',
	styleUrls: ['check-delivery-radius-modal.component.scss'],
})
export class CheckDeliveryRadiusModalComponent extends RepositoryDirective implements OnInit, AfterViewInit {
	@ViewChild(IonInput, { static: true }) inputField: IonInput;
	@Input() selectedVenue: Venue;
	@Input() map: Map;

	loading = false;
	venuesLoaded = false;
	searchTerm: string;
	address: Address;
	showEmpty = false;
	isPlaceValid = true;
	suggestedPlace: Address;
	deliveryVenueAvailable = false;
	predictions: AutocompletePrediction[] = [];
	enrichedDescriptions: Record<string, string> = {};
	addressToString = MapsUtils.addressToString;
	inputValue = '';
	myAddress: Address;
	allVenues: Venue[] = [];
	closestVenue: Venue | null = null;
	private requestId = 0;
	private geocoderCache: Record<string, GeocoderResult> = {};

	constructor(
		protected repository: RepositoryService,
		private translate: TranslateService,
		private snackBar: MatSnackBar,
		private cdr: ChangeDetectorRef,
		private modalCtrl: ModalController,
		public platform: Platform
	) {
		super(repository);
	}

	static async show(modalCtrl: ModalController, address: Address = null, map: Map): Promise<{ venue?: Venue; address?: Address }> {
		const modal = await modalCtrl.create({
			component: CheckDeliveryRadiusModalComponent,
			cssClass: 'delivery-modal',
			componentProps: {
				myAddress: address,
				address: address,
				map,
			},
			backdropDismiss: false
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data;
	}

	async ngOnInit() {
		MapsUtils.getUserGeocode().then(result => {
			this.suggestedPlace = result;
		});
		await this.initAutocomplete();
	}

	largeScreen(): boolean {
		return AppComponent.largeScreen;
	}

	async selectPrediction(prediction: AutocompletePrediction) {
		if (this.loading) return;
		this.loading = true;
		this.inputValue = this.enrichedDescriptions[prediction.place_id] || prediction.description;
		this.predictions = [];
		try {
			const cached = this.geocoderCache[prediction.place_id];
			const result = cached ?? (await MapsUtils.getPlaceByPredictionId(prediction.place_id));
			if (!cached) {
				this.geocoderCache[prediction.place_id] = result;
			}
			MapsUtils.renewSessionToken();
			await this.fillInPlace(MapsUtils.placeToAddress(result));
		} catch (e) {
			console.error(e);
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	async resetSearch() {
		(await this.inputField.getInputElement()).value = '';
		this.searchTerm = '';
		this.venuesLoaded = false;
		this.deliveryVenueAvailable = false;
		this.predictions = [];
		this.enrichedDescriptions = {};
	}

	async loadVenues(address: Address) {
		console.log('selectedVenue', this.selectedVenue);
		this.deliveryVenueAvailable = false;
		this.venuesLoaded = false;
		try {
			const foundVenue = (await this.repository.getVenuesByAddress(environment.customerGroup, address)).find(ven =>
				venueAcceptsOrders(ven, PreorderType.DELIVERY)
			);

			this.deliveryVenueAvailable = foundVenue !== undefined;
			this.venuesLoaded = true;

			if (!foundVenue) {
				this.closestVenue = this.getClosestVenue(this.allVenues, address);
			}
			this.selectedVenue = foundVenue;
		} catch (e) {
			console.error(e);
			this.venuesLoaded = false;
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	chooseClosestVenue() {
		this.modalCtrl.dismiss({
			closest: this.closestVenue,
		});
	}

	getClosestVenue(venues: Venue[], address: Address): Venue | null {
		if (!venues.length) return null;

		let closest = venues[0];
		let minDistance = calculateGeoDistance(address.lat, address.lng, closest.location.coordinates[1], closest.location.coordinates[0]);

		for (let i = 1; i < venues.length; i++) {
			const distance = calculateGeoDistance(
				address.lat,
				address.lng,
				venues[i].location.coordinates[1],
				venues[i].location.coordinates[0]
			);
			if (distance < minDistance) {
				minDistance = distance;
				closest = venues[i];
			}
		}

		return closest;
	}

	async fillInPlace(address: Address) {
		if (!address) {
			return;
		}

		if (!address?.street) return this.snackBar.open('Please enter a street');

		if (ValidationUtils.validateAddress(address) === 'address.number') {
			const updated = await AddressInputDialog.show(this.modalCtrl, address);
			if (!updated) return;
			address = updated;
			address.lat = null;
			address.lng = null;
		}

		if (!address?.lat) {
			try {
				const userNumber = address.number;
				const searchString = [address.street, address.number, address.city, address.country].filter(Boolean).join(', ');
				const result = await MapsUtils.getPlace(searchString);
				address = MapsUtils.placeToAddress(result);
				address.number = userNumber;
			} catch (e) {
				this.loading = false;
				return;
			}
		}
		this.loading = true;
		try {
			await MapsUtils.fillInPlace(this.inputField, address, term => {
				if (term) {
					this.searchTerm = term;
				}
				return this.searchTerm;
			});
			if (!address) {
				await this.executeSearch();
				return;
			}
			this.address = address;
			await this.loadVenues(address);
			this.loading = false;
		} catch (e) {
			this.snackBar.open(this.translate.instant('check_delivery.address_error'), null, {
				duration: 2000,
			});
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	async dismiss(address: Address) {
		this.modalCtrl.dismiss({
			selectedVenue: this.selectedVenue,
			address,
		});
	}

	async executeSearch() {}

	async ngAfterViewInit() {
		const inputElement = await this.inputField.getInputElement();
		inputElement.focus();
	}

	async initAutocomplete() {}

	async delivery() {
		await this.modalCtrl.dismiss({
			venue: this.selectedVenue,
			address: this.address,
		});
	}

	async close() {
		await this.modalCtrl.dismiss();
	}

	result: Address;

	async changeValue(value: any) {
		const searchValue: string = value.detail.value ?? '';
		const currentRequestId = ++this.requestId;

		if (searchValue.trim().length < 3) {
			this.predictions = [];
			this.enrichedDescriptions = {};
			this.loading = false;
			this.cdr.detectChanges();
			return;
		}

		this.loading = true;
		this.selectedVenue = null;
		this.isPlaceValid = true;
		this.searchTerm = searchValue;
		this.cdr.detectChanges();

		try {
			const results = await MapsUtils.getAutocompletePredictions(searchValue);
			if (currentRequestId === this.requestId) {
				this.predictions = results;
				this.enrichedDescriptions = {};
				this.loading = false;
				this.cdr.detectChanges();
				this.enrichPredictionsWithPlz(currentRequestId, results);
			}
		} catch (e) {
			console.error(e);
			if (currentRequestId === this.requestId) {
				this.predictions = [];
				this.loading = false;
				this.cdr.detectChanges();
			}
		}
	}

	private async enrichPredictionsWithPlz(requestId: number, predictions: AutocompletePrediction[]) {
		for (const prediction of predictions.slice(0, 5)) {
			if (requestId !== this.requestId) {
				break;
			}

			let geocoderResult = this.geocoderCache[prediction.place_id];
			if (!geocoderResult) {
				try {
					geocoderResult = await MapsUtils.getPlaceByPredictionId(prediction.place_id);
					this.geocoderCache[prediction.place_id] = geocoderResult;
				} catch {
					continue;
				}
			}

			if (requestId !== this.requestId) {
				break;
			}

			const address = MapsUtils.placeToAddress(geocoderResult);
			if (address.postalCode) {
				const mainText = prediction.structured_formatting?.main_text ?? prediction.description;
				const city = address.city ?? prediction.structured_formatting?.secondary_text ?? '';
				this.enrichedDescriptions[prediction.place_id] = `${mainText}, ${address.postalCode} ${city}`;
				this.cdr.detectChanges();
			}
		}
	}
}
